import React, { useContext, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Row } from 'antd';
import { RootStoreContext } from '../../../app/stores/rootStore';
import { ICollection } from '../../../app/models/Collection';
import { ICompany } from '../../../app/models/company';
import { toast } from 'react-toastify';
import Role from '../../../app/models/role';
import moment from 'moment';

interface IProps {
	company: ICompany;
}

const OnTimeReport: React.FC<IProps> = ({ company }) => {
	const rootStore = useContext(RootStoreContext);
	const { list, adminList } = rootStore.collectionStore;
	const { user } = rootStore.userStore;

	const [exporting, setExporting] = useState(false);
	const [form] = Form.useForm();

	const formatDate = (
		date: Date | undefined,
		time: boolean = false
	): string => {
		if (!date) return '';
		if (time) {
			return moment(date).format('dddd, DD MMM yyyy HH:mm');
		} else {
			return moment(date).format('dddd, DD MMM yyyy');
		}
	};

	const fetchCollections = async (from: Date, to: Date) => {
		setExporting(true);
		try {
			let result: ICollection[] = [];
			if (user?.role !== Role.user) {
				result = (
					await adminList({
						from: from,
						to: to,
						limit: 10000,
						requesterId: company.id
					})
				).data;
			} else {
				result = await list({
					from: from,
					to: to,
					requesterId: undefined,
					limit: 10000
				});
			}

			// Filter collections by the selected company
			const companyCollections = result.filter(
				(c) => c.requester.company.id === company.id
			);

			return companyCollections;
		} catch (error) {
			console.error(error);
			toast.error('Failed to fetch collections');
			return [];
		} finally {
			setExporting(false);
		}
	};

	const prepareCsvData = (collections: ICollection[]) => {
		if (collections.length === 0) {
			toast.warning('No data to export');
			return '';
		}

		// Create CSV header
		const headers = [
			// 'Date Received',
			'Collection date',
			// 'Date Collected',
			'Date Delivered',
			'Service',
			'Waybill Number',
			'Client Identifier',
			'Collection Address',
			'Delivery Address',
			'Contact Detail',
			'Total ex VAT',
			'Charge KG',
			'Comment'
		];

		// Create CSV rows
		const rows = collections.map((collection) => [
			// formatDate(collection.dateTimeRegistered, true),
			formatDate(collection.readyDate),
			// formatDate(collection.pickupDateTime),
			formatDate(collection.deliveryDateTime, true),
			`${collection.serviceType.displayName} (${collection.serviceType.code})`,
			collection.waybill.toString(),
			collection.clientIdentifier || '',
			collection.sender
				? `${collection.sender.name}${
						collection.sender.addressLine1
							? `, ${collection.sender.addressLine1}`
							: ''
				  }${
						collection.sender.addressLine2
							? `, ${collection.sender.addressLine2}`
							: ''
				  }${
						collection.sender.addressLine3
							? `, ${collection.sender.addressLine3}`
							: ''
				  }${
						collection.sender.addressLine4
							? `, ${collection.sender.addressLine4}`
							: ''
				  }, ${collection.sender.city}, ${collection.sender.postalCode}`
				: '',
			collection.receiver
				? `${collection.receiver.name}${
						collection.receiver.addressLine1
							? `, ${collection.receiver.addressLine1}`
							: ''
				  }${
						collection.receiver.addressLine2
							? `, ${collection.receiver.addressLine2}`
							: ''
				  }${
						collection.receiver.addressLine3
							? `, ${collection.receiver.addressLine3}`
							: ''
				  }${
						collection.receiver.addressLine4
							? `, ${collection.receiver.addressLine4}`
							: ''
				  }, ${collection.receiver.city}, ${
						collection.receiver.postalCode
				  }`
				: '',
			collection.sender
				? `${collection.sender.contactPerson} - ${
						collection.sender.contactCell
				  }${
						collection.sender.contactPhone
							? `, ${collection.sender.contactPhone}`
							: ''
				  }${
						collection.sender.contactEmail
							? `, ${collection.sender.contactEmail}`
							: ''
				  }`
				: '',
			collection.myRate ? collection.myRate.toFixed(2) : '0.00',
			collection.shipmentMass || '',
			collection.comment || ''
		]);

		// Combine headers and rows into CSV format
		const csvContent = [
			headers.join(','),
			...rows.map((row) => row.map((field) => `"${field}"`).join(','))
		].join('\n');

		return csvContent;
	};

	const handleExport = async () => {
		try {
			const values = await form.validateFields();
			const from = values.fromDate.toDate();
			const to = values.toDate.toDate();

			// Check if date range is more than a month
			const diffInDays =
				(to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
			if (diffInDays > 31) {
				toast.error('Date range cannot exceed one month');
				return;
			}

			const collections = await fetchCollections(from, to);
			const csvData = prepareCsvData(collections);

			// Create and download CSV file
			const blob = new Blob([csvData], {
				type: 'text/csv;charset=utf-8;'
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.setAttribute('href', url);
			link.setAttribute(
				'download',
				`on-time-report-${company.companyName}-${
					from.toISOString().split('T')[0]
				}-to-${to.toISOString().split('T')[0]}.csv`
			);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error(error);
			toast.error('Failed to export report');
		}
	};

	return (
		<Card>
			<h3>On Time Report</h3>
			<Form form={form} layout="vertical">
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							name="fromDate"
							label="From Date"
							rules={[
								{
									required: true,
									message: 'Please select a from date'
								}
							]}
						>
							<DatePicker style={{ width: '100%' }} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name="toDate"
							label="To Date"
							rules={[
								{
									required: true,
									message: 'Please select a to date'
								}
							]}
						>
							<DatePicker style={{ width: '100%' }} />
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col span={24}>
						<Button
							type="primary"
							onClick={handleExport}
							loading={exporting}
							style={{ marginLeft: '10px' }}
						>
							Export Report
						</Button>
					</Col>
				</Row>
			</Form>
		</Card>
	);
};

export default OnTimeReport;
